import React, { useState, useEffect } from 'react';

// Floating accessibility toolbar, in the spirit of ILOSTAT's approach.
// Everything here works via CSS applied to the whole page, so it doesn't
// require touching any other component -- fully self-contained and
// reversible (the Reset button undoes everything instantly).
const STORAGE_KEY = 'acar_accessibility_prefs';

const defaultPrefs = {
  fontScale: 100,      // percentage, applied to the root font-size
  grayscale: false,
  highContrast: false,
  underlineLinks: false,
};

const loadPrefs = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? { ...defaultPrefs, ...JSON.parse(saved) } : defaultPrefs;
  } catch (e) {
    return defaultPrefs;
  }
};

const AccessibilityToolbar = () => {
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState(loadPrefs);

  // Apply the current preferences to the whole page whenever they change.
  useEffect(() => {
    document.documentElement.style.fontSize = `${prefs.fontScale}%`;

    document.documentElement.classList.toggle('a11y-grayscale', prefs.grayscale);
    document.documentElement.classList.toggle('a11y-high-contrast', prefs.highContrast);
    document.documentElement.classList.toggle('a11y-underline-links', prefs.underlineLinks);

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch (e) {
      // If localStorage is unavailable (e.g. private browsing), the
      // toolbar still works for the current session, it just won't
      // remember the choice on the next visit.
    }
  }, [prefs]);

  const changeFontScale = (delta) => {
    setPrefs((p) => ({
      ...p,
      fontScale: Math.min(150, Math.max(80, p.fontScale + delta)),
    }));
  };

  const toggle = (key) => {
    setPrefs((p) => ({ ...p, [key]: !p[key] }));
  };

  const reset = () => setPrefs(defaultPrefs);

  return (
    <div style={{ position: 'fixed', bottom: '1rem', right: '1rem', zIndex: 50 }}>
      {open && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-4 mb-3 w-64">
          <h2 className="text-sm font-semibold text-gray-800 mb-3">Accessibility</h2>

          <div className="mb-3">
            <span className="text-xs text-gray-600 block mb-1">Text size</span>
            <div className="flex gap-2">
              <button
                onClick={() => changeFontScale(-10)}
                className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm hover:bg-gray-50"
                aria-label="Decrease text size"
              >
                A-
              </button>
              <button
                onClick={() => changeFontScale(10)}
                className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm hover:bg-gray-50"
                aria-label="Increase text size"
              >
                A+
              </button>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700 mb-2 cursor-pointer">
            <input
              type="checkbox"
              checked={prefs.grayscale}
              onChange={() => toggle('grayscale')}
            />
            Grayscale
          </label>

          <label className="flex items-center gap-2 text-sm text-gray-700 mb-2 cursor-pointer">
            <input
              type="checkbox"
              checked={prefs.highContrast}
              onChange={() => toggle('highContrast')}
            />
            High contrast
          </label>

          <label className="flex items-center gap-2 text-sm text-gray-700 mb-3 cursor-pointer">
            <input
              type="checkbox"
              checked={prefs.underlineLinks}
              onChange={() => toggle('underlineLinks')}
            />
            Underline links
          </label>

          <button
            onClick={reset}
            className="w-full text-xs text-acar-navy-700 hover:text-acar-navy-900 underline"
          >
            Reset to default
          </button>
        </div>
      )}

      <button
        onClick={() => setOpen((o) => !o)}
        className="bg-acar-navy-700 text-white rounded-full w-12 h-12 flex items-center justify-center shadow-lg hover:bg-acar-navy-800"
        aria-label={open ? 'Close accessibility options' : 'Open accessibility options'}
        aria-expanded={open}
      >
        <span style={{ fontSize: '18px', fontWeight: 600 }}>Aa</span>
      </button>
    </div>
  );
};

export default AccessibilityToolbar;